<template>
  <div class="row">
    <div class="col-12">
      <b-overlay :show="showLoading">
        <b-card>
          <!-- 第一層：標題 + 操作按鈕 -->
          <div class="row d-flex mb-2 justify-content-between align-items-center">
            <h4 class="col-12 col-xl-auto mb-2 mb-xl-0 font-weight-bold">
              優惠券管理
            </h4>
            <div class="col-12 col-xl-auto d-flex flex-wrap align-items-center justify-content-end">
              <b-button
                class="mb-2 mb-xl-0 mr-2"
                variant="primary"
                @click="handleExport"
              ><i class="mdi mdi-download"></i>匯出</b-button>
              <template v-if="upperCustomer.id">
                <b-button class="mb-2 mb-xl-0" variant="primary" v-b-modal.modal-add>
                  手動給券
                </b-button>
              </template>
            </div>
          </div>
          <div class="mb-3">
            <b-button-group class="coupon-view-switch-group">
              <b-button
                variant="light"
                :class="{ 'coupon-view-switch-active': activeView === 'wallet' }"
                @click="switchView('wallet')"
              >
                目前券匣
              </b-button>
              <b-button
                variant="light"
                :class="{ 'coupon-view-switch-active': activeView === 'history' }"
                @click="switchView('history')"
              >
                使用紀錄
              </b-button>
            </b-button-group>
          </div>

          <!-- 第二層：狀態篩選 + 進階搜尋按鈕 -->
          <template v-if="showFilter">
            <div class="d-flex flex-wrap align-items-center mb-2">
              <b-form-select
                v-if="activeView !== 'history'"
                class="mr-2"
                :options="statusOptions"
                v-model="query.status_for_admin"
                style="max-width: 160px; height: 35px; padding-top: 4px; padding-bottom: 4px;"
                @change="handlePageReset"
              ></b-form-select>
              <b-form-select
                class="mr-3"
                :options="branchOptions"
                v-model="query.branch_id"
                style="max-width: 160px; height: 35px; padding-top: 4px; padding-bottom: 4px;"
                @change="handlePageReset"
              ></b-form-select>
              <b-button v-b-toggle.collapse-advanced-search variant="outline-info" style="height: 34px;">
                <span class="mdi mdi-magnify mt-1 mr-1"></span>進階搜尋
              </b-button>
            </div>

            <!-- 第三層：進階搜尋折疊區塊 -->
            <b-collapse id="collapse-advanced-search" v-model="isAdvancedSearchVisible">
              <b-card>
                <b-form>
                  <div class="d-flex flex-column flex-xl-row align-items-xl-center mb-3">
                    <span class="mr-2 mb-2 mb-xl-0 font-weight-bold text-nowrap">篩選</span>
                    <b-form-select
                      v-model="query.date_type"
                      :options="dateTypeOptions"
                      class="advanced-search-control mr-2 mb-2 mb-xl-0"
                      style="max-width: 160px;"
                    ></b-form-select>
                    <span class="mr-2 mb-2 mb-xl-0 text-nowrap">從</span>
                    <div class="mr-2 mb-2 mb-xl-0" style="min-width: 150px;">
                      <datepicker
                        placeholder="開始日期"
                        bootstrap-styling
                        format="yyyy-MM-dd"
                        :value="query.after"
                        @selected="handleRedeemAfterSelected"
                      ></datepicker>
                    </div>
                    <span class="mr-2 mb-2 mb-xl-0 text-nowrap">到</span>
                    <div class="mb-2 mb-xl-0" style="min-width: 150px;">
                      <datepicker
                        placeholder="結束日期"
                        bootstrap-styling
                        format="yyyy-MM-dd"
                        :value="query.before"
                        @selected="handleRedeemBeforeSelected"
                      ></datepicker>
                    </div>
                  </div>
                  <div class="d-flex">
                    <b-button
                      variant="primary"
                      class="mb-2 mb-xl-0 mr-2"
                      @click="reset"
                    ><i class="mdi mdi-restart"></i> 重置條件</b-button>
                    <b-button
                      variant="primary"
                      class="mb-2 mb-xl-0"
                      @click="handlePageReset"
                    >
                      <i class="fa fa-search"></i> 搜尋
                    </b-button>
                  </div>
                </b-form>
              </b-card>
            </b-collapse>

            <!-- 第四層：關鍵字搜尋框 -->
            <b-card class="mb-2">
              <b-input-group>
                <b-form-input
                  v-model="query.keyword_v2"
                  placeholder="搜尋會員名稱、手機、外部編碼"
                  v-on:keyup.enter="handlePageReset"
                ></b-form-input>
                <b-input-group-append>
                  <b-button @click="handlePageReset"
                  ><i class="fa fa-search"></i
                  ></b-button>
                </b-input-group-append>
              </b-input-group>
            </b-card>
          </template>

          <b-card-text>
            <div class="row">
              <div class="col-12">
                <b-overlay rounded="sm">
                  <b-table
                    striped
                    hover
                    responsive
                    :items="redeemCodes"
                    :fields="filteredFields"
                    @sort-changed="handleSort"
                  >
                    <template #cell(actions)="data">
                      <b-button
                        size="sm"
                        :variant="data.item.redeem_at ? 'outline-danger' : 'outline-success'"
                        :disabled="processingRedeemCodeId === data.item.hash_id"
                        @click="handleToggleRedeem(data.item)"
                      >
                        {{ data.item.redeem_at ? "取消核銷" : "手動核銷" }}
                      </b-button>
                    </template>
                  </b-table>
                </b-overlay>
              </div>
            </div>
            <CustomPagination
              :currentPage="query.page"
              :totalRows="totalRows"
              :perPage="query.per_page"
              @page-change="handlePageChange"
              @per-page-change="handlePerPageChange"
            />
          </b-card-text>
        </b-card>
      </b-overlay>
    </div>

    <b-modal id="modal-add" title="手動給券">
      <b-form-group
        label-cols="12"
        label-cols-lg="2"
        label-size="sm"
        label="優惠券"
      >
        <vue-typeahead-bootstrap
          :data="filteredRedeem"
          v-model="redeemKeyWord"
          @hit="selectedRedeemCodeBundle = $event"
          :showOnFocus="true"
          :serializer="(item) => item.name"
          placeholder="請輸入獎項名稱"
        >
          <template slot="suggestion" slot-scope="{ data }">
            <div class="d-flex align-items-center">
              <b-badge variant="outline-dark mr-2">獎項種類</b-badge>
              <span class="font-weight-bold">{{ data.name }}</span>
            </div>
            <div class="small py-2">
              {{ data.description }}
            </div>
            <div class="small font-weight-bold">
              <span>{{ data.start_at }}</span>
              <span class="mx-2">-</span>
              <span>{{ data.end_at }}</span>
            </div>
          </template>
        </vue-typeahead-bootstrap>
      </b-form-group>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-danger"
          size="sm"
          class="float-right"
          @click="cancel()"
        >
          關閉
        </b-button>
        <b-button
          size="sm"
          variant="outline-primary"
          @click="assignRedeemCodeByBundle"
        >
          確定
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import redeemCodeApi from "@/apis/redeem-code";
import redeemCodeBundleApi from "@/apis/redeemCodeBundle";
import { startOfDay, endOfDay, format} from "date-fns";
import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import {mapGetters} from 'vuex';
import Datepicker from "vuejs-datepicker";
import { paginationMixin } from "@/mixins/pagination";

export default {
  mixins: [paginationMixin],
  components: {
    CustomPagination,
    VueTypeaheadBootstrap,
    Datepicker,
  },
  props: {
    showFilter: {
      type: Boolean,
      default: true,
    },
    excludeFields: {
      type: Array,
      default: () => [],
    },
    upperCustomer: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const baseFields = [
      {
        key: "redeem_code_bundle.name",
        label: "優惠券名稱",
      },
      {
        key: "code",
        label: "序號",
      },
      {
        key: "customer",
        label: "會員名稱",
        formatter: (value) => {
          if (value && value.name) {
            return this.hiddenString(value.name, 20);
          }
          return "";
        },
      },
      {
        key: "branch.name",
        label: "分店名稱",
      },
      {
        key: "branch.branch_code",
        label: "分店代號",
      },
      {
        key: "belong_at",
        label: "指派時間",
        formatter: (value) => {
          return value
            ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
            : null;
        },
        sortable: true,
      },
      {
        key: "apply_at",
        label: "領取時間",
        formatter: (value) => {
          return value
            ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
            : null;
        },
        sortable: true,
      },
      {
        key: "redeem_at",
        label: "使用時間",
        formatter: (value) => {
          return value
            ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
            : null;
        },
        sortable: true,
      },
      {
        key: "is_enabled",
        label: "啟用/停用",
        formatter: (value) => {
          return value ? "啟用" : "停用";
        },
      },
      {
        key: "status",
        label: "使用狀況",
        formatter: (value, key, item) => {
          switch (true) {
            case !!item.redeem_at:
              return "已兌換";
            case !!item.apply_at:
              return "已領取";
            case !!item.belong_at:
              return "已歸戶";
            default:
              return "未領取";
          }
        },
      },
      {
        key: "actions",
        label: "操作",
      },
    ];
    return {
      showLoading: false,
      isAdvancedSearchVisible: false,
      activeView: "wallet",
      dateTypeOptions: [
        { value: null, text: "選擇篩選時段" },
        { value: "redeem", text: "使用時間" },
        { value: "apply", text: "領取時間" },
        { value: "belong", text: "指派時間" },
      ],
      statusOptions: [
        { value: "above_belonged", text: "全部使用狀況" },
        { value: "belonged", text: "未領取" },
        { value: "applied", text: "已領取" },
        { value: "redeemed", text: "已使用" },
      ],
      totalRows: 0,
      redeemCodes: [],
      canAssignRedeemCodesBundles: [],
      fields: baseFields,
      baseFields: baseFields,
      customers: [],
      selectedRedeemCodeBundle: null,
      redeemKeyWord: "",
      processingRedeemCodeId: null,
      initialized: false,
      skipQueryWatcher: false,
      query: {
        page: 1,
        per_page: 10,
        sort_by: "redeem_at",
        order_by: "desc",
        keyword_v2: null,
        status_for_admin: "above_belonged",
        date_type: null,
        branch_id: null,
        after: null,
        before: null,
      },
    };
  },
  computed: {
    filteredFields() {
      return this.fields.filter(
        (field) => !this.excludeFields.includes(field.key)
      );
    },
    filteredRedeem() {
      const list = Array.isArray(this.canAssignRedeemCodesBundles)
        ? this.canAssignRedeemCodesBundles
        : [];

      return list.filter(a => a.name?.includes(this.redeemKeyWord));
    },
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    ...mapGetters('general', [
      'myBranches',
    ]),
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
    }),
    branchOptions() {
      let options = this.myBranches
        .map(branch => {
          let branchName = `${branch.branch_code} ${branch.name}`
          return {
            value: branch.id,
            text: branchName
          }
        })
      options = [
        {
            value: null,
            text: '請選擇分店'
        },
        ...options
      ]
      return options
    },
  },
  created() {
    this.query = updateQueryFromRoute(
      this.$route,
      this.$store,
      this.query
    );
    if (this.query.status_for_admin === "redeemed") {
      this.activeView = "history";
    }
  },
  mounted() {
    this.fetchRedeemCodes();
    if (this.upperCustomer.merchant?.id) {
      this.fetchCanAssignRedeemCodes();
    }
    this.fetchFields();
    this.initialized = true;
    this.checkAndExpandAdvancedSearch();
  },
  watch: {
    upperCustomer(value) {
      if (value.id) {
        this.fetchCanAssignRedeemCodes();
      }
    },
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        if (this.skipQueryWatcher) {
          this.skipQueryWatcher = false;
          return;
        }
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    "$route.query": {
      handler(newQuery) {
        if (!this.initialized) return;
        if (Object.keys(newQuery).length !== 0) {
          this.query = { ...newQuery };
          this.activeView =
            this.query.status_for_admin === "redeemed" ? "history" : "wallet";
        }
        this.fetchRedeemCodes();
        this.checkAndExpandAdvancedSearch();
      },
      deep: true,
    },
  },
  methods: {
    checkAndExpandAdvancedSearch() {
      // 當有進階搜尋參數（date_type、after、before）時，自動展開進階搜尋
      const q = this.$route.query;
      const hasAdvancedParams = q.date_type || q.after || q.before;

      if (hasAdvancedParams && !this.isAdvancedSearchVisible) {
        this.$nextTick(() => {
          this.isAdvancedSearchVisible = true;
        });
      }
    },
    async fetchRedeemCodes() {
      this.showLoading = true;

      let params = {
        ...this.query,
        // 若 upperCustomer.id 為 null，api 參數會被轉成 null 字串，所以這邊多一個判斷將 null 變成空字串
        customer_id: this.upperCustomer.id || "",
      };

      if (this.query.date_type === 'belong') {
        params.belong_after = this.query.after;
        params.belong_before = this.query.before;
      } else if (this.query.date_type === 'apply') {
        params.apply_after = this.query.after;
        params.apply_before = this.query.before;
      } else if (this.query.date_type === 'redeem') {
        params.redeem_after = this.query.after;
        params.redeem_before = this.query.before;
      } else {
        if (this.query.status_for_admin === 'belonged') {
          params.belong_after = this.query.after;
          params.belong_before = this.query.before;
        }

        if (this.query.status_for_admin === 'applied') {
          params.apply_after = this.query.after;
          params.apply_before = this.query.before;
        }

        if (this.query.status_for_admin === 'redeemed') {
          params.redeem_after = this.query.after;
          params.redeem_before = this.query.before;
        }
      }

      params.active_view = this.activeView;

      const response = await redeemCodeApi.getRedeemCodes(params);
      this.redeemCodes = response.data.data;
      this.totalRows = response.data.meta.total;
      this.showLoading = false;
    },
    async fetchCanAssignRedeemCodes() {
      this.showLoading = true;
      const response = await redeemCodeBundleApi.getCanAssignRedeemCodeBundles({
        merchant_id: this.upperCustomer.merchant.id,
      });

      const result = response.data.data;
      this.canAssignRedeemCodesBundles = Array.isArray(result) ? result : [];

      this.canAssignRedeemCodesBundles.forEach((item) => {
        item.start_at = format(new Date(item.start_at), "yyyy-MM-dd");
        item.end_at = format(new Date(item.end_at), "yyyy-MM-dd");
      });

      this.showLoading = false;
    },
    hiddenString(value, length) {
      value = value || "";
      const splittedValue = [...value];
      if (splittedValue.length <= length) {
        return value;
      }
      return splittedValue.slice(0, length).join("") + "...";
    },
    async assignRedeemCodeByBundle() {
      try {
        await redeemCodeBundleApi.assignRedeemCodeByBundle(
          this.selectedRedeemCodeBundle.id,
          { customer_id: this.upperCustomer.id }
        );
        this.$swal.fire({
          title: "新增成功",
          type: "success",
        });
      } catch (error) {
        this.$swal.fire({
          title: "新增失敗",
          type: "error",
        });
        console.log(error);
      } finally {
        this.$bvModal.hide("modal-add");
        this.fetchCanAssignRedeemCodes();
        this.selectedRedeemCodeBundle = null;
      }
    },
    async handleToggleRedeem(item) {
      const isCancel = !!item.redeem_at;
      const actionText = isCancel ? "取消核銷" : "手動核銷";
      const couponName = item.redeem_code_bundle?.name || "此優惠券";

      const result = await this.$swal.fire({
        title: actionText,
        text: `【${couponName}】確定要${actionText}嗎？`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "確定",
        cancelButtonText: "取消",
      });

      if (!result.value) return;

      try {
        this.showLoading = true;
        this.processingRedeemCodeId = item.hash_id;
        if (isCancel) {
          await redeemCodeApi.cancelRedeemCode(item.hash_id);
        } else {
          await redeemCodeApi.redeemCode(item.hash_id);
        }
        this.$swal.fire({
          title: `${actionText}成功`,
          text: `【${couponName}】`,
          type: "success",
        });
        await this.fetchRedeemCodes();
      } catch (error) {
        this.$swal.fire({
          title: `${actionText}失敗`,
          text: `【${couponName}】`,
          type: "error",
        });
        console.log(error);
      } finally {
        this.showLoading = false;
        this.processingRedeemCodeId = null;
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageResetWithoutReload();
      this.fetchRedeemCodes();
    },
    handleRedeemAfterSelected(after) {
      this.query.after = format(startOfDay(after), 'yyyy-MM-dd HH:mm:ss');
    },
    handleRedeemBeforeSelected(before) {
      this.query.before = format(endOfDay(before), 'yyyy-MM-dd HH:mm:ss');
    },
    reset() {
      this.query.after = null;
      this.query.before = null;
      this.query.status_for_admin = this.activeView === "history" ? "redeemed" : "above_belonged";
      this.query.date_type = null;
      this.query.branch_id = null;
      this.query.keyword_v2 = null;
      this.query.page = 1;
      this.handlePageReset();
    },
    switchView(view) {
      if (this.activeView === view) return;

      this.activeView = view;
      this.query.status_for_admin = view === "history" ? "redeemed" : "above_belonged";

      if (parseInt(this.query.page, 10) !== 1) {
        this.skipQueryWatcher = true;
        this.query.page = 1;
      }

      this.handlePageResetWithoutReload();
      this.fetchRedeemCodes();
    },
    async handleExport() {
      let params = {
        ...this.query,
        customer_id: this.upperCustomer.id || "",
      };

      if (this.query.date_type === 'belong') {
        params.belong_after = this.query.after;
        params.belong_before = this.query.before;
      } else if (this.query.date_type === 'apply') {
        params.apply_after = this.query.after;
        params.apply_before = this.query.before;
      } else if (this.query.date_type === 'redeem') {
        params.redeem_after = this.query.after;
        params.redeem_before = this.query.before;
      } else {
        if (this.query.status_for_admin === 'belonged') {
          params.belong_after = this.query.after;
          params.belong_before = this.query.before;
        }

        if (this.query.status_for_admin === 'applied') {
          params.apply_after = this.query.after;
          params.apply_before = this.query.before;
        }

        if (this.query.status_for_admin === 'redeemed') {
          params.redeem_after = this.query.after;
          params.redeem_before = this.query.before;
        }
      }

      params.active_view = this.activeView;

      try {
        this.showLoading = true;
        await redeemCodeApi.exportRedeemCodes(params).then(() => {
          this.$swal({
            title: '成功',
            text: "可至“我的主控台”查看紀錄及下載檔案",
            showCancelButton: true,
            cancelButtonText: '確認',
            type: 'success',
            confirmButtonText: '前往查看',
          }).then((result)=>{
            if (result.value) {
              this.$router.push({
                name: "MyConsoleDownloadFileList",
                params: { org_code: this.$route.params.org_code },
              });
            }
          });
        });
      } catch (e) {
        console.log(e)
      }
      this.showLoading = false
    },
    async fetchFields() {
      let normalizeProfileFields = await this.normalizeProfileFields();
      // 找到插入的位置
      const insertIndex = this.baseFields.findIndex(
        (field) => field.key === "branch.name"
      );

      // 創建新的欄位陣列，插入 normalizeProfileFields
      this.fields = [
        ...this.baseFields.slice(0, insertIndex), // 從開頭到插入點之前的欄位
        ...normalizeProfileFields,          // 插入 normalizeProfileFields
        ...this.baseFields.slice(insertIndex),   // 插入點之後的欄位
      ];
    },
    async normalizeProfileFields() {
      const customerList = this.getModuleConfig("customer", "reward.customer_list");

      const profileMappings = customerList?.[0]?.member_fields ?? [];

      if (profileMappings) {
        return profileMappings.map((mapping) => {
          if (mapping.matched_key.startsWith("extra.")) {
            const key = mapping.matched_key.split("extra.")[1];
            return {
              key: `profiles.${key}`,
              label: mapping.title,
            };
          } else {
            return {
              key: mapping.matched_key,
              label: mapping.title,
            };
          }
        });
      } else {
        return [];
      }
    },
  },
};
</script>

<style scoped>
/* 進階搜尋區塊 - 統一表單控制項高度 */
.advanced-search-control {
  height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
}

#collapse-advanced-search >>> .vdp-datepicker input {
  height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.coupon-view-switch-group {
  border: 1px solid #2d8be4;
  border-radius: 6px;
  overflow: hidden;
}

.coupon-view-switch-group .btn {
  min-width: 110px;
  border: 0;
  border-radius: 0 !important;
  color: #2d8be4;
  background-color: #fff;
}

.coupon-view-switch-group .btn + .btn {
  border-left: 1px solid #2d8be4;
}

.coupon-view-switch-group .coupon-view-switch-active {
  color: #fff;
  background-color: #2d8be4;
}
</style>
